/**
 * E002-T034: ExecutionService
 * E002-T037: Batching & Error Handling
 *
 * Manages automation execution lifecycle including:
 * - Execute (all filtered rows, manual trigger)
 * - Trigger-initiated runs (scheduler / webhook)
 * - Runtime controls (Pause/Resume/Stop)
 * - Progress tracking
 * - Integration with LoggingService for Run-Level logging
 * - Batching: Process rows in configurable batch sizes
 * - Retry: Exponential backoff for failed API calls
 * - On Failure: continue/pause/stop behavior
 */

import { manualRunPages } from '@streamlain/automation-safety-contract';
import { Service } from '@toeverything/infra';
import { LiveData } from '@toeverything/infra';

import { defaultWorkspaceApiClient } from '../../cloud';
import type { InputStageLog, OutputStageLog, ProcessStageLog } from './logging';
import { LoggingService } from './logging';
import { workflowWorkspaceRoutes } from './workflow-workspace-routes';

/**
 * sl-g3on.5.2: i18n-friendly warning shape produced by the backend.
 *
 * Mirrors `ExecutionWarning` from
 * `packages/backend/server/src/interfaces/automation/execution-engine.interface.ts`.
 * The frontend cannot import server types, so we re-declare the shape here
 * and keep it byte-compatible.
 */
export interface ExecutionWarning {
  readonly key: string;
  readonly params: Readonly<Record<string, string | number | null>>;
}

// =============================================================================
// Batching & Error Handling Types (T037)
// =============================================================================

export type OnFailureMode = 'continue' | 'stop';

export interface RetryConfig {
  enabled: boolean;
  maxAttempts: number;
  initialDelayMs: number;
  backoffMultiplier: number;
  maxDelayMs: number;
}

export interface BatchConfig {
  /** Number of rows to process per run (1-100, default: 1) */
  batchSize: number;
  /** row dispatches one source row per runner call; batch dispatches multiple normal source rows in one runner call. */
  executionMode: 'row' | 'batch';
  retry: RetryConfig;
  onFailure: OnFailureMode;
}

export type NormalizableBatchConfig = Partial<Omit<BatchConfig, 'retry'>> & {
  retry?: Partial<RetryConfig> | null;
};

// Default batch config (T047)
export const DEFAULT_BATCH_CONFIG: BatchConfig = {
  batchSize: 1,
  executionMode: 'row',
  retry: {
    enabled: false,
    maxAttempts: 3,
    initialDelayMs: 1000,
    backoffMultiplier: 2,
    maxDelayMs: 30000,
  },
  onFailure: 'stop',
};

export function normalizeBatchConfig(
  config?: NormalizableBatchConfig | null
): BatchConfig {
  return {
    ...DEFAULT_BATCH_CONFIG,
    ...(config ?? {}),
    batchSize: config?.batchSize ?? DEFAULT_BATCH_CONFIG.batchSize,
    executionMode: config?.executionMode ?? DEFAULT_BATCH_CONFIG.executionMode,
    retry: {
      ...DEFAULT_BATCH_CONFIG.retry,
      ...(config?.retry ?? {}),
    },
    onFailure: config?.onFailure ?? DEFAULT_BATCH_CONFIG.onFailure,
  };
}

/**
 * Execution state machine:
 * idle → running → completed
 *       ↓    ↑
 *     queued
 *       ↓
 *   cancelled / failed
 */
export type ExecutionStatus =
  | 'idle'
  | 'running'
  | 'queued'
  | 'completed'
  | 'cancelled'
  | 'failed';

/**
 * sl-1ahs.7.4: Harmonised with backend ExecutionMode.
 * `'execute'` = manual full execution; `'trigger'` = scheduler- or
 * webhook-initiated run. The removed `'test'` mode was dropped together with
 * the automation_test tool (sl-1ahs.7.2).
 */
export type ExecutionMode = 'execute' | 'trigger';

export interface ExecutionProgress {
  /** Total number of rows to process */
  total: number;
  /** Number of rows processed */
  processed: number;
  /** Number of successful rows */
  succeeded: number;
  /** Number of failed rows */
  failed: number;
  /** Current row being processed (for display) */
  currentRow?: Record<string, unknown>;
  /** Percentage complete (0-100) */
  percentage: number;
  /** sl-3nkm.8: Live media progress details from ffmpeg socket events. */
  media?: {
    stepId: string;
    stepName: string;
    /**
     * sl-3nkm.8.4: Per-row runner execution id. Unique per row even
     * when the workflow step id is reused across batch rows. Used by
     * the timeout-warning anchor in `ExecutionPanel` to re-arm per
     * row.
     */
    executionId: string;
    percent?: number;
    currentTimeMs?: number;
    totalTimeMs?: number;
    timeRemainingMs?: number;
    bytesDone?: number;
    bytesTotal?: number;
    bitrate?: string;
    fps?: number;
  };
}

export interface ExecutionResult {
  /** Row identifier */
  rowId: string;
  /** Whether the execution succeeded */
  success: boolean;
  /** Input data sent to workflow */
  input: Record<string, unknown>;
  /** Output data received from workflow */
  output?: Record<string, unknown>;
  /** Error message if failed */
  error?: string;
  /** Execution timestamp */
  timestamp: number;
}

export interface ExecutionState {
  /** Current execution status */
  status: ExecutionStatus;
  /** Execution mode — `'execute'` for manual runs, `'trigger'` for scheduled/webhook-initiated runs */
  mode: ExecutionMode | null;
  /** Progress information */
  progress: ExecutionProgress;
  /** Results from execution */
  results: ExecutionResult[];
  /** Error message if status is 'failed' */
  error?: string;
  /** Start timestamp */
  startedAt?: number;
  /** End timestamp */
  endedAt?: number;
}

/**
 * Options for execution with logging support
 */
export interface ExecutionOptions {
  /** Workspace ID - required for backend persistence */
  workspaceId: string;
  /** Document ID - required for backend persistence */
  docId: string;
  /** Input stage data for logging */
  inputStage?: InputStageLog;
  /** Process stage data for logging (will be updated after execution) */
  processStage?: Omit<ProcessStageLog, 'duration' | 'status'>;
  /** Output stage data for logging (will be updated after execution) */
  outputStage?: Omit<OutputStageLog, 'rowsWritten' | 'rowsFailed' | 'results'>;
  /** Callback after each row is successfully executed - used for writeback */
  onRowComplete?: (
    rowId: string,
    input: Record<string, unknown>,
    output: Record<string, unknown>
  ) => void;
  /** Batching configuration (T037) */
  batchConfig?: BatchConfig;
}

// =============================================================================
// Retry Helper (T037)
// =============================================================================

/**
 * Execute with retry and exponential backoff
 */
async function executeWithRetry<T>(
  fn: () => Promise<T>,
  retryConfig: RetryConfig,
  signal: AbortSignal
): Promise<T> {
  if (!retryConfig.enabled) {
    return fn();
  }

  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= retryConfig.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry on abort
      if (signal.aborted) {
        throw lastError;
      }

      // Last attempt, throw error
      if (attempt === retryConfig.maxAttempts) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        retryConfig.initialDelayMs *
          Math.pow(retryConfig.backoffMultiplier, attempt),
        retryConfig.maxDelayMs
      );

      console.log(
        `[Execution] Retry ${attempt + 1}/${retryConfig.maxAttempts} after ${delay}ms`
      );

      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay));

      // Check abort again after delay
      if (signal.aborted) {
        throw lastError;
      }
    }
  }

  throw lastError ?? new Error('Retry failed');
}

const defaultProgress: ExecutionProgress = {
  total: 0,
  processed: 0,
  succeeded: 0,
  failed: 0,
  percentage: 0,
};

const defaultState: ExecutionState = {
  status: 'idle',
  mode: null,
  progress: { ...defaultProgress },
  results: [],
};

export class ExecutionService extends Service {
  /**
   * Execution states per block (blockId -> state)
   */
  private readonly states = new Map<string, LiveData<ExecutionState>>();

  /**
   * Abort controllers for cancellation
   */
  private readonly abortControllers = new Map<string, AbortController>();

  private request(
    workspaceId: string,
    path: string,
    init: RequestInit = {}
  ): Promise<Response> {
    const method = init.method ?? 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId,
      path,
      init,
      category: 'foreground-action',
      key: `${method}:${path}`,
      mode: 'foreground',
      priority: 'foreground',
    });
  }

  /**
   * LoggingService for Run-Level logging
   */
  private readonly loggingService: LoggingService;

  constructor(loggingService: LoggingService) {
    super();
    this.loggingService = loggingService;
  }

  /**
   * Get reactive execution state for a block
   */
  getState$(blockId: string): LiveData<ExecutionState> {
    let state$ = this.states.get(blockId);
    if (!state$) {
      state$ = new LiveData<ExecutionState>({ ...defaultState });
      this.states.set(blockId, state$);
    }
    return state$;
  }

  /**
   * A manual run over the rows the user chose in the source table. The
   * backend queues one run per row in "next row" mode (one per batch in
   * batch mode); their progress arrives like that of any run. A choice
   * larger than one page goes in pages of one request, the last page queues
   * the runs over all of them.
   */
  async runRows(
    workspaceId: string,
    docId: string,
    blockId: string,
    rowIds: readonly string[]
  ): Promise<
    | { success: true; runs: number; rows: number }
    | { success: false; error: string }
  > {
    const clientRequestId = crypto.randomUUID();
    const pages = manualRunPages(rowIds);
    for (const [page, pageRowIds] of pages.entries()) {
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/run-rows`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            docId,
            clientRequestId,
            page,
            pages: pages.length,
            rowIds: pageRowIds,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error:
            result.message ||
            result.error ||
            `Server error (${response.status})`,
        };
      }
      if (page === pages.length - 1) {
        return { success: true, runs: result.runs, rows: result.rows };
      }
    }
    return { success: false, error: 'No rows chosen' };
  }

  /**
   * The choice over the rows the import brake held back: run them (one run
   * per row) or discard them; either switches the automation on again.
   */
  async resolveHeldRows(
    workspaceId: string,
    docId: string,
    blockId: string,
    action: 'run' | 'discard'
  ): Promise<
    | { success: true; runs: number; rows: number }
    | { success: false; error: string }
  > {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/held-rows`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, action }),
      }
    );
    const result = await response.json();
    if (!response.ok) {
      return {
        success: false,
        error:
          result.message || result.error || `Server error (${response.status})`,
      };
    }
    return { success: true, runs: result.runs, rows: result.rows };
  }

  /**
   * sl-rwr: Execute automation via unified trigger endpoint.
   * ONE mechanism for frontend AND backend.
   * Backend loads ALL config from Y.js (sourceTableId, outputMode, webhookUrl, etc.)
   */
  async executeViaBackend(
    workspaceId: string,
    docId: string,
    blockId: string,
    mode: 'execute',
    /**
     * Expected row count known from the preflight preview. Seeds
     * `progress.total` so the sidebar shows "0 / N" while the backend is
     * still iterating — without it the sidebar flashes "0 / 0" for the
     * entire duration of a synchronous backend run (no streaming progress
     * signal exists yet).
     */
    expectedRowCount?: number
  ): Promise<{
    success: boolean;
    error?: string;
    queued?: boolean;
    pending?: boolean;
    queuePosition?: number;
    estimatedWaitMs?: number;
    rowsProcessed?: number;
    rowsWritten?: number;
    /**
     * sl-g3on.5.2: i18n-friendly warnings forwarded by the backend.
     * The frontend renders these via `WarningRow` in execution-panel.tsx
     * (sl-g3on.5.3) using the workflow definition cached in Y.js to
     * resolve field IDs to human-readable names.
     */
    warnings?: ExecutionWarning[];
  }> {
    const state$ = this.getState$(blockId);
    const currentState = state$.value;

    // Prevent concurrent executions
    if (currentState.status === 'running' || currentState.status === 'queued') {
      throw new Error('Execution already in progress');
    }

    // Update state to running
    state$.next({
      ...currentState,
      status: 'running',
      mode,
      progress: {
        ...defaultProgress,
        total: expectedRowCount ?? 0,
      },
      results: [],
    });

    try {
      // sl-rwr: Unified trigger endpoint - backend loads EVERYTHING from Y.js
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/trigger-now`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            docId,
            mode,
            clientRequestId: crypto.randomUUID(),
          }),
        }
      );

      const result = await response.json();

      // Handle HTTP errors (e.g. 400 from syncAndPrepare failures)
      if (!response.ok) {
        const errorMessage =
          result.message || result.error || `Server error (${response.status})`;

        state$.next({
          ...currentState,
          status: 'idle',
          mode: null,
          progress: { ...defaultProgress },
          results: [],
          error: errorMessage,
        });

        return {
          success: false,
          error: errorMessage,
        };
      }

      if (result.pending === true) {
        state$.next({
          ...state$.value,
          status: 'running',
          mode,
          error: undefined,
        });
        return result;
      }

      if (result.success === false && result.queued === true) {
        state$.next({
          ...currentState,
          status: 'queued',
          mode,
          progress: {
            ...defaultProgress,
            total: expectedRowCount ?? 0,
          },
          results: [],
          error: undefined,
        });

        return result;
      }

      if (result.success === false) {
        const errorMessage = result.error || 'Execution failed';
        state$.next({
          ...currentState,
          status: 'failed',
          mode,
          progress: {
            ...defaultProgress,
            total: expectedRowCount ?? result.rowsProcessed ?? 0,
            processed: result.rowsProcessed ?? 0,
            failed: result.rowsFailed ?? 0,
          },
          results: [],
          error: errorMessage,
          endedAt: Date.now(),
        });

        return {
          ...result,
          error: errorMessage,
        };
      }

      // Update state to idle (execution complete)
      state$.next({
        ...currentState,
        status: 'idle',
        mode: null,
        progress: {
          ...defaultProgress,
          total: result.rowsProcessed ?? 0,
          processed: result.rowsProcessed ?? 0,
          succeeded: result.rowsWritten ?? 0,
        },
        results: [],
      });

      return result;
    } catch (error) {
      state$.next({
        ...currentState,
        status: 'idle',
        mode: null,
        progress: { ...defaultProgress },
        results: [],
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Start full execution (all rows)
   */
  async executeAll(
    blockId: string,
    rows: Array<Record<string, unknown>>,
    executeRow: (
      row: Record<string, unknown>,
      signal: AbortSignal
    ) => Promise<Record<string, unknown>>,
    options: ExecutionOptions
  ): Promise<void> {
    await this.execute(blockId, rows, executeRow, 'execute', options);
  }

  /**
   * Internal execution logic
   */
  private async execute(
    blockId: string,
    rows: Array<Record<string, unknown>>,
    executeRow: (
      row: Record<string, unknown>,
      signal: AbortSignal
    ) => Promise<Record<string, unknown>>,
    mode: ExecutionMode,
    options: ExecutionOptions
  ): Promise<void> {
    const state$ = this.getState$(blockId);
    const currentState = state$.value;

    // Prevent concurrent executions
    if (currentState.status === 'running') {
      throw new Error('Execution already in progress');
    }

    // Setup abort controller
    const abortController = new AbortController();
    this.abortControllers.set(blockId, abortController);

    const startTime = Date.now();

    // Start logging
    this.loggingService.startLog(blockId, mode);

    // Log input stage if provided
    if (options?.inputStage) {
      this.loggingService.logInputStage(blockId, options.inputStage);
    }

    // Initialize state
    state$.next({
      status: 'running',
      mode,
      progress: {
        total: rows.length,
        processed: 0,
        succeeded: 0,
        failed: 0,
        percentage: 0,
      },
      results: [],
      startedAt: startTime,
    });

    const results: ExecutionResult[] = [];

    try {
      for (let i = 0; i < rows.length; i++) {
        // Check for abort
        if (abortController.signal.aborted) {
          state$.next({
            ...state$.value,
            status: 'cancelled',
            endedAt: Date.now(),
          });

          // Complete log as cancelled and persist to backend
          this.completeAndPersist(blockId, options, 'cancelled', startTime);
          return;
        }

        const row = rows[i];
        // Row ID can be in 'id', '_rowId', or fallback to index
        const rowId =
          (row['_rowId'] as string) ?? (row['id'] as string) ?? `row-${i}`;

        // Update current row
        state$.next({
          ...state$.value,
          progress: {
            ...state$.value.progress,
            currentRow: row,
          },
        });

        let result: ExecutionResult;

        // Get batch config (T037)
        const batchConfig = options?.batchConfig ?? DEFAULT_BATCH_CONFIG;

        try {
          // Execute with retry (T037)
          const output = await executeWithRetry(
            () => executeRow(row, abortController.signal),
            batchConfig.retry,
            abortController.signal
          );

          result = {
            rowId,
            success: true,
            input: row,
            output,
            timestamp: Date.now(),
          };

          // Call onRowComplete callback for writeback
          if (options?.onRowComplete && output) {
            try {
              options.onRowComplete(rowId, row, output);
            } catch (callbackError) {
              console.error(
                '[Execution] onRowComplete callback error:',
                callbackError
              );
            }
          }
        } catch (error) {
          result = {
            rowId,
            success: false,
            input: row,
            error: error instanceof Error ? error.message : String(error),
            timestamp: Date.now(),
          };

          // On Failure handling (T037)
          if (batchConfig.onFailure === 'stop') {
            // Stop execution immediately
            console.log(
              '[Execution] Row failed, stopping execution (onFailure=stop)'
            );
            results.push(result);

            // Update progress before stopping
            const processed = i + 1;
            state$.next({
              ...state$.value,
              status: 'failed',
              progress: {
                total: rows.length,
                processed,
                succeeded: state$.value.progress.succeeded,
                failed: state$.value.progress.failed + 1,
                percentage: Math.round((processed / rows.length) * 100),
                currentRow: undefined,
              },
              results: [...state$.value.results, result],
              error: result.error,
              endedAt: Date.now(),
            });

            this.completeAndPersist(
              blockId,
              options,
              'failed',
              startTime,
              result.error
            );
            return;
          }
          // onFailure === 'continue': just continue with next row
        }

        results.push(result);

        // Update progress
        const processed = i + 1;
        const succeeded =
          state$.value.progress.succeeded + (result.success ? 1 : 0);
        const failed = state$.value.progress.failed + (result.success ? 0 : 1);

        state$.next({
          ...state$.value,
          progress: {
            total: rows.length,
            processed,
            succeeded,
            failed,
            percentage: Math.round((processed / rows.length) * 100),
            currentRow: undefined,
          },
          results: [...state$.value.results, result],
        });
      }

      const endTime = Date.now();
      const duration = endTime - startTime;

      // Log process stage
      if (options?.processStage) {
        const failCount = results.filter(r => !r.success).length;

        this.loggingService.logProcessStage(blockId, {
          ...options.processStage,
          duration,
          status: failCount === 0 ? 'success' : 'error',
          response:
            results.length > 0
              ? (results[results.length - 1].output as Record<string, unknown>)
              : undefined,
          error:
            failCount > 0
              ? `${failCount} of ${results.length} rows failed`
              : undefined,
        });
      }

      // Log output stage
      if (options?.outputStage) {
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;

        this.loggingService.logOutputStage(blockId, {
          ...options.outputStage,
          rowsWritten: successCount,
          rowsFailed: failCount,
          results: results.map(r => ({
            rowId: r.rowId,
            success: r.success,
            error: r.error,
          })),
        });
      }

      // sl-oe5x fix: a run with per-row failures must NOT report as
      // 'completed' — the orchestrator finished its lifecycle but the
      // workflow itself failed on every row the user cares about.
      // Flip the terminal status so the HistoryPanel success-rate
      // reflects actual per-row outcomes.
      const failedCount = results.filter(r => !r.success).length;
      const allFailed = failedCount === results.length && results.length > 0;
      const terminalStatus: 'completed' | 'failed' = allFailed
        ? 'failed'
        : 'completed';
      const errorMsg =
        failedCount > 0
          ? `${failedCount} of ${results.length} rows failed`
          : undefined;

      state$.next({
        ...state$.value,
        status: terminalStatus,
        endedAt: endTime,
        ...(errorMsg && { error: errorMsg }),
      });

      this.completeAndPersist(
        blockId,
        options,
        terminalStatus,
        startTime,
        errorMsg
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      // Execution failed
      state$.next({
        ...state$.value,
        status: 'failed',
        error: errorMessage,
        endedAt: Date.now(),
      });

      // Complete log as failed and persist to backend
      this.completeAndPersist(
        blockId,
        options,
        'failed',
        startTime,
        errorMessage
      );
    } finally {
      // Cleanup
      this.abortControllers.delete(blockId);
    }
  }

  /**
   * Stop execution.
   * Aborts the local execution loop AND sends cancel to backend (pod delete).
   * sl-16e5: Backend cancel kills the K8s pod via SIGTERM → graceful shutdown.
   */
  stop(blockId: string, workspaceId?: string): void {
    const state$ = this.getState$(blockId);

    if (state$.value.status === 'running' || state$.value.status === 'queued') {
      // Abort the local execution loop
      const abortController = this.abortControllers.get(blockId);
      if (abortController) {
        abortController.abort();
      }

      state$.next({
        ...state$.value,
        status: 'cancelled',
        endedAt: Date.now(),
      });

      // sl-16e5: Also cancel on backend (kill pod). Fire-and-forget.
      if (workspaceId) {
        void this.request(
          workspaceId,
          workflowWorkspaceRoutes.cancel(workspaceId, blockId),
          {
            method: 'POST',
          }
        ).catch(() => {
          // Best effort — local abort already updated the UI
        });
      }
    }
  }

  /**
   * Reset execution state to idle
   */
  reset(blockId: string): void {
    const state$ = this.getState$(blockId);
    state$.next({ ...defaultState });
  }

  /**
   * Update execution status from backend (via WebSocket).
   * Used when backend sends completion/failure events.
   */
  setStatusFromBackend(
    blockId: string,
    status: 'completed' | 'failed' | 'cancelled',
    error?: string
  ): void {
    const state$ = this.getState$(blockId);
    const currentState = state$.value;

    // Only update if currently running (prevent stale updates)
    if (currentState.status === 'running' || currentState.status === 'queued') {
      console.debug(
        `[ExecutionService] Backend status update: ${blockId} → ${status}`
      );
      state$.next({
        ...currentState,
        status,
        endedAt: Date.now(),
        // sl-3nkm Phase 4b audit F8: Clear the media progress snapshot
        // on terminal status. A media step that failed or was
        // cancelled before reaching 100% leaves `progress.media`
        // latched on the last frame; without this reset the timeout
        // anchor in ExecutionPanel would treat the stale
        // mediaExecutionId as still active and mis-track time for the
        // next run or any non-media step that follows.
        progress: {
          ...currentState.progress,
          media: undefined,
        },
        ...(status === 'failed' && error ? { error } : {}),
      });
    }
  }

  /**
   * A queued run left the workflow queue and runs on a runner pod now
   * (tsk-am-wait-in-log). The backend signals it with the process stage of
   * its log; until then the run waits and its run time does not count.
   */
  markRunStarted(blockId: string): void {
    const state$ = this.getState$(blockId);
    const currentState = state$.value;
    if (currentState.status !== 'queued') return;
    state$.next({ ...currentState, status: 'running' });
  }

  /**
   * The run waits for a runner pod (again): it found none after it left the
   * queue. Its run time stops until the backend dispatches it.
   */
  markRunWaiting(blockId: string): void {
    const state$ = this.getState$(blockId);
    const currentState = state$.value;
    if (currentState.status !== 'running') return;
    state$.next({ ...currentState, status: 'queued' });
  }

  applyExecutionProgress(
    blockId: string,
    progress: {
      stepId: string;
      stepName: string;
      executionId: string;
      percent?: number;
      currentTimeMs?: number;
      totalTimeMs?: number;
      timeRemainingMs?: number;
      bytesDone?: number;
      bytesTotal?: number;
      bitrate?: string;
      fps?: number;
    }
  ): void {
    const state$ = this.getState$(blockId);
    const currentState = state$.value;
    if (currentState.status !== 'running') return;

    const percentage = progress.percent ?? currentState.progress.percentage;
    state$.next({
      ...currentState,
      progress: {
        ...currentState.progress,
        percentage,
        total: 100,
        processed: Math.max(
          currentState.progress.processed,
          Math.round(percentage)
        ),
        media: progress,
      },
    });
  }

  /**
   * Check if execution can be started
   */
  canStart(blockId: string): boolean {
    const state = this.getState$(blockId).value;
    return (
      state.status === 'idle' ||
      state.status === 'completed' ||
      state.status === 'cancelled' ||
      state.status === 'failed'
    );
  }

  /**
   * Check if execution can be stopped
   */
  canStop(blockId: string): boolean {
    const status = this.getState$(blockId).value.status;
    return status === 'running' || status === 'queued';
  }

  /**
   * Complete log (move from activeLog to history)
   * Note: Backend persistence is handled by the caller (e.g., ExecutionPanel)
   * after fetching workflow engine execution details for nodeSteps.
   */
  private completeAndPersist(
    blockId: string,
    _options: ExecutionOptions,
    status: 'completed' | 'failed' | 'cancelled',
    _startedAt: number,
    error?: string
  ): void {
    this.loggingService.completeLog(blockId, status, error);
    // Note: persistToBackend is now called from ExecutionPanel.handleExecutionComplete
    // AFTER fetching nodeSteps from workflow engine, so all execution data is included.
  }

  /**
   * Cleanup when block is deleted
   */
  deleteState(blockId: string): void {
    this.stop(blockId);
    this.states.delete(blockId);
    this.abortControllers.delete(blockId);

    // Also cleanup logs
    this.loggingService.deleteBlockLogs(blockId);
  }

  // Note: Backend persistence is now handled by ExecutionPanel.handleExecutionComplete()
  // AFTER fetching workflow engine execution details for nodeSteps, so all execution data is included.
}
